import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Alert, Anchor, Card, Group, Skeleton, Stack, Text, Title } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import DocumentGroupCard from '@/components/app/company/DocumentGroupCard';
import ExpiryDialog from '@/components/app/company/ExpiryDialog';
import { companyService } from '@/api/services/CompanyService';
import { getApiMessage, isNetworkError, isServiceUnavailable } from '@/api/errors';
import { AGENCY_AGREEMENT_QUERY_KEY, useAgencyAgreement, useAgentInfo } from '@/hooks/useAgency';
import { useJustSignedDocTypes } from '@/hooks/useJustSignedDocTypes';
import { REQUIRED_DATA_QUERY_KEY, useRequiredData } from '@/hooks/useRequiredData';
import { ROUTES } from '@/routes/paths';
import type { RequiredField } from '@/api/services/OrderService';
import type { DocFile, DocType } from '@/types/company';
import { formatOrgName } from '@/lib/format';
import InfoRow from '@/components/common/InfoRow';

// Базовые данные, без которых нельзя подписывать договор (→ ссылки на нужные экраны).
const BASE_FIELD_LINKS: Partial<Record<RequiredField, { key: string; to: string }>> = {
  TAX_SYSTEM: { key: 'app.banner.taxSystem', to: `${ROUTES.APP_COMPANY}#tax` },
  CONTACT_PHONE: { key: 'app.banner.contactPhone', to: `${ROUTES.APP_COMPANY}?addContact=true` },
  BANK_REQUISITS: { key: 'app.banner.bank', to: `${ROUTES.APP_COMPANY_BANKING}?createNew=true` },
};

// G4 — Агентский договор: реквизиты агента + (если хватает базовых данных) документы договора.
export default function AgencyTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const agent = useAgentInfo();
  const docs = useAgencyAgreement();
  const justSigned = useJustSignedDocTypes(docs.data);
  const required = useRequiredData();
  const [expiry, setExpiry] = useState<{ docId: number; initial: DocFile } | null>(null);
  const [progress, setProgress] = useState<{ docType: DocType; percent: number } | null>(null);

  const fields = required.data?.requiredUserDataFields ?? [];
  const missing = fields.filter((field): field is keyof typeof BASE_FIELD_LINKS => field in BASE_FIELD_LINKS);
  const messages = required.data?.otherMessages ?? [];
  const statusText = messages.includes('RESEND_AGENCY_AGREEMENT')
    ? t('app.banner.resendAgency')
    : messages.includes('AGENCY_AGREEMENT')
      ? t('app.company.agency.statusRequired')
      : null;

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: AGENCY_AGREEMENT_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: REQUIRED_DATA_QUERY_KEY });
  };
  const onError = (error: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(error)
        ? t('auth.errors.unavailable')
        : isNetworkError(error)
          ? t('auth.errors.generic')
          : (getApiMessage(error) ?? t('auth.errors.generic')),
    });
  };

  const upload = useMutation({
    mutationFn: (payload: { docType: DocType; files: File[] }) =>
      companyService.uploadDocuments(payload.docType, payload.files, (percent) =>
        setProgress({ docType: payload.docType, percent }),
      ),
    onMutate: (payload) => setProgress({ docType: payload.docType, percent: 0 }),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'lime', message: t('app.company.accreditation.uploaded') });
    },
    onError,
    onSettled: () => setProgress(null),
  });
  const remove = useMutation({
    mutationFn: (docId: number) => companyService.deleteDocument(docId),
    onSuccess: invalidate,
    onError,
  });
  const expiryMutation = useMutation({
    mutationFn: (payload: { docId: number; isPerpetual: boolean; date?: string }) =>
      companyService.setExpiredDate(payload.docId, payload.isPerpetual, payload.date),
    onSuccess: () => {
      invalidate();
      setExpiry(null);
    },
    onError,
  });

  async function download(docType: DocType, docId: number): Promise<void> {
    try {
      await companyService.downloadDocument(docType, docId);
    } catch (error) {
      onError(error);
    }
  }

  function confirmDelete(docId: number): void {
    modals.openConfirmModal({
      title: t('app.company.accreditation.deleteTitle'),
      children: <Text size="sm">{t('app.company.accreditation.deleteConfirm')}</Text>,
      labels: { confirm: t('app.company.banking.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(docId),
    });
  }

  const baseComplete = missing.length === 0;

  return (
    <Stack gap="md">
      {statusText ? (
        <Alert color="wheat" variant="light" icon={<IconAlertTriangle size={18} />}>
          {statusText}
        </Alert>
      ) : null}

      {/* Реквизиты агента */}
      {agent.isLoading ? (
        <Skeleton h={140} />
      ) : (
        <Card withBorder padding="lg">
          <Stack gap="xs">
            <Title order={4}>{t('app.company.agency.agentTitle')}</Title>
            <InfoRow label={t('app.company.agency.agentName')} value={formatOrgName(agent.data?.name)} />
            <InfoRow label={t('auth.fields.inn')} value={agent.data?.inn} mono />
            <InfoRow label={t('app.company.card.ogrn')} value={agent.data?.ogrn} mono />
            <InfoRow label={t('app.company.card.regAddress')} value={agent.data?.regAddress} />
          </Stack>
        </Card>
      )}

      {/* Не хватает базовых данных → список недостающего вместо блока документов */}
      {!baseComplete ? (
        <Alert color="wheat" variant="light" title={t('app.company.agency.missingTitle')}>
          <Stack gap={6}>
            {missing.map((field) => {
              const link = BASE_FIELD_LINKS[field];
              return link ? (
                <Group key={field} gap="sm" wrap="nowrap">
                  <Text size="sm" style={{ flex: 1 }}>
                    {t(link.key)}
                  </Text>
                  <Anchor component={Link} to={link.to} size="sm" fw={500} c="brand.7">
                    {t('app.banner.goto')}
                  </Anchor>
                </Group>
              ) : null;
            })}
          </Stack>
        </Alert>
      ) : docs.isLoading ? (
        <Skeleton h={160} />
      ) : (
        <>
          <Text c="gray.7" size="sm">
            {t('app.company.agency.intro')}
          </Text>
          {docs.data?.map((group) => (
            <DocumentGroupCard
              key={group.docType}
              group={group}
              uploadPending={upload.isPending && upload.variables?.docType === group.docType}
              uploadPercent={progress?.docType === group.docType ? progress.percent : undefined}
              justSigned={justSigned.has(group.docType)}
              onDownload={download}
              onUpload={(docType, files) => upload.mutate({ docType, files })}
              onDelete={confirmDelete}
              onSetExpiry={(file) => setExpiry({ docId: file.id, initial: file })}
            />
          ))}
        </>
      )}

      <ExpiryDialog
        opened={expiry !== null}
        initial={expiry?.initial ?? null}
        loading={expiryMutation.isPending}
        onSave={(isPerpetual, date) => {
          if (expiry) {
            expiryMutation.mutate({ docId: expiry.docId, isPerpetual, date });
          }
        }}
        onClose={() => setExpiry(null)}
      />
    </Stack>
  );
}
